import { ComponentType, useState } from 'react';

/**
 * A `dynamic()` registry component that can also render synchronously once its module has been
 * preloaded. `next/dynamic` renders `null` on its first mount even when the module is already in
 * memory: its loader only resolves in a microtask and the component picks that up in a re-render
 * that React runs after the empty commit has been painted. For the search list that empty frame
 * collapsed the results column (home → /s/… CLS ≈ 0.65, docs/performance.md §۳.۲۱).
 *
 * `dynamicComponent` stays the SSR/hydration path (Next only preloads modules it sees inside a
 * `dynamic(() => import(...))` call). `load` must import the same module; after `preload()` has
 * resolved, new mounts render the loaded component directly. Each instance keeps the choice it made
 * at mount, so a mounted list is never swapped (remounted) under the user.
 */
export function preloadable(dynamicComponent: ComponentType<any>, load: () => Promise<ComponentType<any>>) {
  let Loaded: ComponentType<any> | null = null;
  let pending: Promise<void> | null = null;

  const Preloadable = (props: Record<string, any>) => {
    const [Direct] = useState(() => Loaded);
    const Component = Direct ?? dynamicComponent;
    return <Component {...props} />;
  };

  const preload = () =>
    (pending ??= load().then(
      C => {
        Loaded = C;
      },
      error => {
        pending = null;
        throw error;
      },
    ));

  return { Component: Preloadable, preload };
}
